import { PeriodFilter } from "@/components/date-filter/PeriodFilter.tsx";
import { ApiDateFilterMode } from "@/components/date-filter/date-filter.type.ts";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { EmployeeColumnNumberRangeModal } from "@/pages/employees/index/EmployeeColumnNumberRangeModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeColumnFilterValueProps } from "@/pages/employees/index/employee-column-filter-renderers.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import {
  ApiEmployeeColumnFilterType,
  ApiEmployeeColumnOptionId,
} from "@/types/employees/employee-column.types.ts";
import {
  ButtonIcon,
  FontAwesomeIcon,
  Input,
  Multiselect,
  SelectItem,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** Любой из вариантов: опции справочника, кастомного поля или enum из каталога. */
function SelectFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  const options = useMemo<SelectItem[]>(
    () => (column.options ?? []).map((option) => ({ id: option.id, title: option.title })),
    [column.options]
  );

  return (
    <Multiselect
      label={column.title}
      options={options}
      values={filter.option_ids ?? []}
      // id опции уезжает как пришёл: у справочников число, у enum строка.
      // Приводить тип нельзя — бэк отвергает нечисловой id справочника
      onChange={(value) =>
        onChange({
          option_ids: value.filter(
            (item): item is ApiEmployeeColumnOptionId => item !== null && item !== undefined
          ),
        })
      }
      searchable
      isClearable
      disabled={!options.length}
      emptyTitle={options.length ? t("common:select") : t("common:nothing_found")}
    />
  );
}

/** Выбор сотрудников существующим пикером: тот же виджет, что у старых фильтров. */
function EmployeeFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const currentIds = filter.employee_ids ?? [];

  const search = useEmployeeSearchInline();
  const { data: employeesLoaded } = useEmployeesByIds(currentIds, currentIds.length > 0);

  const selectedOptions = useMemo<SelectItem[]>(
    () =>
      (employeesLoaded ?? []).map((employee) => ({
        id: employee.id,
        title: renderEmployeeName(employee),
      })),
    [employeesLoaded, renderEmployeeName]
  );

  return (
    <Multiselect
      label={column.title}
      options={selectedOptions}
      values={currentIds}
      onChange={(value) =>
        onChange({
          employee_ids: value.filter((item): item is number => typeof item === "number"),
        })
      }
      searchOptions={search.options}
      onSearchOptions={search.search}
      isLoading={search.isLoading}
      showAvatar
      showCaption
      loadOptionsOnOpen
      searchable
      isClearable
      emptyTitle={t("common:select")}
    />
  );
}

function DateFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  return (
    <PeriodFilter
      title={column.title}
      availableModes={[
        column.id === "employee.birth_date" ? ApiDateFilterMode.day : ApiDateFilterMode.date,
      ]}
      value={filter.date_filter}
      onChange={(value) => onChange({ date_filter: value })}
    />
  );
}

/** Диапазон: любая из границ может быть пустой, тогда сравнение с одной стороны. */
function NumberFilterValue(props: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { column, filter } = props;
  const display = [
    filter.number_from !== undefined
      ? `${t("common:number_from_short")} ${filter.number_from}`
      : "",
    filter.number_to !== undefined ? `${t("common:number_to_short")} ${filter.number_to}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const open = () => openModal(EmployeeColumnNumberRangeModal, props, { size: "md" });

  return (
    // Сброс — отдельная кнопка рядом, а не внутри: вложенный <button> невалиден
    <div className="relative">
      <button
        type="button"
        className="flex h-14 w-full cursor-pointer items-center justify-between gap-sm border-b border-dark/10 pr-10 text-left"
        aria-haspopup="dialog"
        onClick={open}
      >
        <span className="flex min-w-0 flex-col">
          <span className={display ? "truncate text-sm text-secondary" : "truncate text-secondary"}>
            {column.title}
          </span>
          {display && <span className="truncate font-semibold">{display}</span>}
        </span>
      </button>
      <span className="absolute top-1/2 right-0 flex -translate-y-1/2 items-center">
        {display ? (
          <ButtonIcon
            variant="plain"
            size="sm"
            aria-label={t("common:reset")}
            icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
            onClick={() => props.onChange({})}
          />
        ) : (
          <FontAwesomeIcon
            className="pointer-events-none text-dark/70"
            icon="fa-light fa-chevron-down"
          />
        )}
      </span>
    </div>
  );
}

function TextFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  return (
    <Input
      label={column.title}
      value={filter.text ?? ""}
      placeholder={t("core:column_filter_text_placeholder")}
      // Подстрока без учёта регистра: точные значения — это text_array,
      // отдельного виджета под них в панели нет
      onChange={(event) => onChange({ text: event.currentTarget.value || undefined })}
    />
  );
}

const CURRENCY_COLUMNS = new Set(["employee.additional_compensation.currency"]);

/**
 * Контрол значения по умолчанию — по `filter_type` колонки каталога. У bool
 * условия со значением нет: «да», «нет» и «пусто» выбираются самим условием.
 */
export function EmployeeColumnFilterValue(props: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  // Валюта в каталоге — текстовая колонка (код валюты): выбранные коды уходят
  // точным совпадением в text_array. option_ids бэк у текстовой колонки не читает
  if (CURRENCY_COLUMNS.has(props.column.id)) {
    return (
      <CurrencySelect
        isMulti
        emptyTitle={t("common:select")}
        label={props.column.title}
        values={props.filter.text_array ?? []}
        searchable
        isClearable
        onChange={(values) => {
          const codes = values.filter((value): value is string => typeof value === "string");
          props.onChange({ text_array: codes.length ? codes : undefined });
        }}
      />
    );
  }
  switch (props.column.filter_type) {
    case ApiEmployeeColumnFilterType.select:
      return <SelectFilterValue {...props} />;
    case ApiEmployeeColumnFilterType.employee:
      return <EmployeeFilterValue {...props} />;
    case ApiEmployeeColumnFilterType.date:
      return <DateFilterValue {...props} />;
    case ApiEmployeeColumnFilterType.number:
      return <NumberFilterValue {...props} />;
    case ApiEmployeeColumnFilterType.text:
      return <TextFilterValue {...props} />;
    default:
      return null;
  }
}
